<template>
    <BasicDragablePanel
        v-if="isShowingAddCommentBox"
        v-clickoutside="closeDialog"
        :initialPos="defaultPosition"
        :onClose="closeDialog"
        class="simple-comment-dialog"
    >
        <main slot="body">
            <div class="simple-comment-dialog__container">
                <div class="simple-comment-dialog__header">
                    <span class="simple-comment-dialog__header-avator" :style="{ 'background-color': colorSet[userEmailFirstLetter] }">{{ userEmailFirstLetter }}</span>
                    <span class="simple-comment-dialog__header-content">{{ userNickName }}</span>
                </div>
                <textarea
                    v-model.trim="content"
                    :placeholder="$t('snapBusiness.comment.addComment')"
                    :maxlength="maxLength"
                    class="simple-comment-dialog__textarea"
                />
                <!--快捷注释-->
                <div class="simple-comment-dialog__quick-list pa-mt-1 pa-rounded pa-bg-gray-20 pa-py-1 pa-pl-2 pa-pr-1">
                    <div class="simple-comment-dialog__quick-list__header pa-flex pa-items-center pa-justify-between">
                        <span class="pa-text-[12px] pa-text-gray-90">{{ $t('snapBusiness.comment.quickComment') }}</span>
                        <button
                            class="th-btn th-btn-only-icon th-btn-plain pa-h-5 pa-min-h-[20px] pa-w-5"
                            @click="openEditQuickCommentDialog"
                        >
                            <svg width="20" height="20" class="th-btn-icon">
                                <use xlink:href="#SolidEdit" />
                            </svg>
                        </button>
                    </div>
                    <div v-if="quickComment?.length" class="simple-comment-dialog__quick-list__body pa-mt-1">
                        <p
                            v-for="(content, index) in quickComment"
                            :key="index"
                            class="simple-comment-dialog__quick-list-item pa-my-0.5 pa-mr-1 pa-inline-block pa-cursor-pointer pa-rounded pa-bg-gray-30 pa-p-1 pa-text-[12px] pa-leading-4 pa-text-gray-1100"
                            @click="setQuickComment(content)"
                        >
                            {{ content }}
                        </p>
                    </div>
                </div>
                <div class="simple-comment-dialog__footer">
                    <div>
                        <button class="pt-ui-btn" data-type="default" @click="closeDialog">
                            {{ $t('snapBusiness.global.btn.cancel') }}
                        </button>
                        <button
                            class="pt-ui-btn"
                            data-type="primary"
                            :disabled="!content.length || isLoading"
                            @click="addComment"
                        >
                            {{ $t('snapBusiness.global.btn.submit') }}
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="isLoading" class="simple-comment-dialog__mask" />
        </main>
    </BasicDragablePanel>
</template>

<script>
import _ from 'lodash';
import BasicDragablePanel from '../BasicDragablePanel';
import { MessageBox } from 'element-ui';
import CommentService from '../../../services/comment-service';
import WordingService from '../../../services/wording-service';
import clickoutside from 'element-ui/src/utils/clickoutside';
import WordingHandler from '../../../utils/wording-handler';
import colorSet from '../../../../../../components/config/color-set';
import { MAX_LIMIT_COMMENT_TEXT_LENGTH } from '../../../constants';
import getUserDisplayInfo from 'library-@/utils/getUserDisplayInfo';
import { useCommentDialog } from './composables/use-comment-dialog';
import '@patsnap-ui/icon/assets/solid/edit.svg';

export default {
    name: 'AddCommentDialog',
    components: {
        BasicDragablePanel,
    },
    directives: {
        clickoutside,
    },
    props: {
        patentId: {
            type: String,
            default: '',
        },
        commentPermission: {
            type: Object,
            require: true,
        },
        spaceId: {
            type: String,
        },
        folderId: {
            type: String,
        },
        maxLength: {
            type: Number,
            default: MAX_LIMIT_COMMENT_TEXT_LENGTH,
        },
    },
    setup() {
        const {
            isShowingAddCommentBox,
            addCommentBoxPosition,
            setIsShowingAddCommentBox,
            setIsShowingEditQuickComment,
        } = useCommentDialog();

        return {
            isShowingAddCommentBox,
            addCommentBoxPosition,
            setIsShowingAddCommentBox,
            setIsShowingEditQuickComment,
        };
    },
    data() {
        return {
            currentUser: {},
            colorSet: colorSet.mapping,
            content: '',
            isLoading: false,
            quickComment: [],
            sourceFrom: '',
        };
    },
    computed: {
        defaultPosition() {
            const [clientHeight, clientWidth] = [document.body.clientHeight, document.body.clientWidth];
            const [selfWidth, selfHeight, headerHeight] = [280, 220, 64];
            // 总宽 - patentList - 自己宽度
            let left = this.addCommentBoxPosition.left || (document.body.clientWidth - 296 - 476);
            let top = this.addCommentBoxPosition.top || 94;
            // 需要计算一下碰撞值
            if (top + selfHeight > clientHeight - headerHeight) {
                top = clientHeight - selfHeight - headerHeight;
            }
            if (left + selfWidth > clientWidth) {
                left = clientWidth - selfWidth;
            }
            return [left, top];
        },
        isPersonalAccount() {
            return !_.get(this, 'currentUser.company_id');
        },
        currentVisibleScopeKey() {
            const currentType = this.isPersonalAccount ? 'PRIVATE' : 'ORG';
            return this.spaceId ? 'SPACE': currentType;
        },

    },
    watch: {
        isShowingAddCommentBox(val) {
            if (val) {
                this.quickComment = WordingHandler.patentInfo.quickComment || [];
                this.$nextTick(() => {
                    this.$el.querySelector('.simple-comment-dialog__textarea') && this.$el.querySelector('.simple-comment-dialog__textarea').focus();
                });
            }
        },
        patentId: {
            handler(val) {
                this.commentService = new CommentService(val, this.spaceId, this.folderId);
                this.wordingService = new WordingService(val);
            },
            immediate: true,
        },
    },
    created() {
        this.currentUser = _.get(window, 'zPREFACE.accountInfo');
        this.$confirm = MessageBox.confirm;
        const userInfo = _.get(window, 'zPREFACE.accountInfo', '');
        this.userNickName = getUserDisplayInfo(userInfo);
        this.userEmailFirstLetter = this.userNickName ? this.userNickName.slice(0, 1).toUpperCase() : '';
    },
    methods: {
        ignore(e) {
            // 检查点击的元素的类名，如果是 "ignore-click"，则忽略这次点击
            return e.target.className === 'ignore-click';
        },
        openDialog() {
            this.setIsShowingAddCommentBox(true);
        },
        closeDialog() {
            if (this.isLoading) return;
            this.content = '';
            this.setIsShowingAddCommentBox(false);
        },
        async addComment() {
            let comment = {};
            const offsetMeta = WordingHandler.addOffsetMeta;
            const patentInfo = WordingHandler.patentInfo;
            this.isLoading = true;

            if (WordingHandler.addWordingId) {
                // 直接 comment
                comment = await this.commentService.createComment(this.content, '', '', WordingHandler.addWordingId, this.currentVisibleScopeKey);
                if (!comment.comment_id) {
                    this.isLoading = false;
                    this.closeDialog();
                    return;
                }
                WordingHandler.commentCallback(WordingHandler.addWordingId, comment.comment_id);
            } else {
                // 先划段再 comment
                const param = {
                    patent_id: patentInfo.patent_id,
                    related_patent_id: patentInfo.related_patent_id,
                    workspace_id: this.spaceId,
                    folder_id: this.folderId,
                    wording_type: 'COMMENT',
                    source: patentInfo.source,
                    lang: patentInfo.lang,
                    start: offsetMeta.startMeta,
                    end: offsetMeta.endMeta,
                    content: offsetMeta.fullText,
                };
                const { wording_id } = await this.wordingService.setWording(param);
                if (!wording_id) {
                    this.isLoading = false;
                    this.closeDialog();
                    return;
                }
                try {
                    comment = await this.commentService.createComment(this.content, '', '', wording_id, this.currentVisibleScopeKey);
                } catch (e) {
                    console.log(e);
                }
                if (!comment.comment_id) {
                    this.isLoading = false;
                    this.closeDialog();
                    return;
                }
                WordingHandler.commentCallback(wording_id, comment.comment_id);
            }
            this.isLoading = false;
            this.closeDialog();
            // 成功后需要通知 comment 弹框更新数据
            comment.content = WordingHandler.addOffsetMeta.fullText;
            comment.source = WordingHandler.patentInfo.source;
            comment.content_lang = WordingHandler.patentInfo.content_lang;
            WordingHandler.$emit('addNewComment', comment, patentInfo.patent_id);
            // 用户引导 8月发版移除
            if (!this.folderId || (this.folderId && this.commentPermission.isOwner)) {
                WordingHandler.$emit('commentSuccess');
            }
            window.rvTrack('Clicked Add Wording Comment', { type: patentInfo.source, content: offsetMeta.fullText, isFromWorkspace: !!this.spaceId });
        },
        openEditQuickCommentDialog() {
            this.setIsShowingEditQuickComment(true);
            this.closeDialog();
        },
        setQuickComment(text) {
            this.content = text;
        },
    },
};
</script>

<style lang="scss">
.simple-comment-dialog {
    width: 320px;
    color: #08163a;
    .dragpan-header {
        padding: 8px;
        padding-bottom: 0;
    }
    &__container {
        padding: 0 16px 16px 16px;
        position: relative;
        z-index: 1000;
    }
    &__header {
        &-avator {
            height: 24px;
            width: 24px;
            line-height: 24px;
            text-align: center;
            border-radius: 12px;
            color: #fff;
            background: #1976D2;
            overflow: hidden;
            display: inline-block;
            vertical-align: middle;
        }
        &-content {
            display: inline-block;
            line-height: 24px;
            margin-left: 4px;
            vertical-align: middle;
        }
    }
    &__footer {
        margin-top: 8px;
        display: flex;
        align-items: center;
        justify-content: end;
        .workspace-dropdown-2-container {
            .dropdown-select {
                svg.patsnap-biz-icon {
                    color: $status-info-primary;
                }
            }
        }
        .patsnap-ui-button {
            margin-right: 4px;
            &:last-of-type {
                margin-right: 0;
            }
        }
    }
    &__textarea {
        padding: 0;
        margin-top: 8px;
        width: 100%;
        resize: none;
        height: 40px;
        border: none;
        caret-color: #1976d2;
        &::placeholder {
            color: #b3bac5;
        }
    }
    &__mask {
        position: fixed;
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;
        background-color: rgba(0, 0, 0, 0);
        z-index: 999;
    }
}
</style>
